import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';
import { X, Camera, Sparkles, Heart, MessageCircle, Send, Crown, Check, ChevronRight } from 'lucide-react';
import { toast } from 'sonner';

const ICONS = {
  Camera: Camera,
  Sparkles: Sparkles,
  Heart: Heart,
  MessageCircle: MessageCircle,
  Send: Send,
  Crown: Crown,
};

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

export const OnboardingChecklist = () => {
  const navigate = useNavigate();
  const [data, setData] = useState(null);
  const [expanded, setExpanded] = useState(true);

  useEffect(() => {
    loadStatus();
  }, []);

  const loadStatus = async () => {
    try {
      const token = localStorage.getItem('mamalink_token') || sessionStorage.getItem('mamalink_token');
      const res = await axios.get(`${BACKEND_URL}/api/onboarding/status`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setData(res.data);
    } catch (e) {
      // silencieux
    }
  };

  const handleDismiss = async () => {
    try {
      const token = localStorage.getItem('mamalink_token') || sessionStorage.getItem('mamalink_token');
      await axios.post(`${BACKEND_URL}/api/onboarding/dismiss`, {}, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setData(null);
    } catch (e) {
      setData(null);
    }
  };

  // Ne rien afficher si dismissed, complet, ou pas chargé
  if (!data || data.dismissed || data.is_complete) return null;

  const percent = Math.round((data.completed_count / data.total) * 100);

  return (
    <div
      className="mb-6 bg-gradient-to-br from-[#FFF5EE] via-[#FFEFE5] to-[#FFEAE0] rounded-3xl p-5 border-2 border-[#FFD4B8] shadow-lg"
      data-testid="onboarding-checklist"
    >
      <div className="flex items-start justify-between mb-3">
        <div className="flex-1">
          <div className="flex items-center gap-2 mb-1">
            <span className="text-2xl">🌸</span>
            <h3 className="font-bold text-[#5C3D36] text-lg">
              {percent === 100 ? 'Bravo, ton profil est complet ! 🎉' : 'Bienvenue sur MamaLink !'}
            </h3>
          </div>
          <p className="text-sm text-[#8B7355]">
            {data.completed_count}/{data.total} étapes complétées — débloque ton expérience complète 💕
          </p>
        </div>
        <button
          onClick={handleDismiss}
          className="p-2 hover:bg-white/50 rounded-full transition-colors"
          data-testid="onboarding-dismiss"
          aria-label="Fermer"
        >
          <X className="w-4 h-4 text-[#9D8189]" />
        </button>
      </div>

      {/* Progress bar */}
      <div className="w-full bg-white/60 rounded-full h-2 mb-4 overflow-hidden">
        <div
          className="h-full bg-gradient-to-r from-[#FFB5A7] to-[#FCD5CE] transition-all duration-500"
          style={{ width: `${percent}%` }}
        />
      </div>

      {expanded && (
        <div className="space-y-2">
          {data.steps.map((step) => {
            const Icon = ICONS[step.icon] || Sparkles;
            return (
              <button
                key={step.id}
                onClick={() => !step.done && navigate(step.link)}
                disabled={step.done}
                className={`w-full flex items-center gap-3 p-3 rounded-xl transition-all ${
                  step.done
                    ? 'bg-[#F0E6E0]/40 cursor-default'
                    : 'bg-white hover:bg-[#FFF9F5] cursor-pointer hover:shadow-md'
                }`}
                data-testid={`onboarding-step-${step.id}`}
              >
                <div className={`w-9 h-9 rounded-full flex items-center justify-center flex-shrink-0 ${
                  step.done
                    ? 'bg-[#FFB5A7]/30 text-[#FFB5A7]'
                    : 'bg-[#FFB5A7]/20 text-[#FFB5A7]'
                }`}>
                  {step.done ? <Check className="w-5 h-5" /> : <Icon className="w-5 h-5" />}
                </div>
                <span className={`flex-1 text-left text-sm ${
                  step.done ? 'text-[#B9A39A] line-through' : 'text-[#5C3D36] font-medium'
                }`}>
                  {step.label}
                </span>
                {!step.done && <ChevronRight className="w-4 h-4 text-[#9D8189]" />}
              </button>
            );
          })}
        </div>
      )}

      <button
        onClick={() => setExpanded(!expanded)}
        className="mt-3 text-xs text-[#9D8189] hover:text-[#5C3D36] font-medium"
        data-testid="onboarding-toggle"
      >
        {expanded ? 'Réduire ▲' : 'Voir les étapes ▼'}
      </button>
    </div>
  );
};

export default OnboardingChecklist;
